import {
  ChildFieldResColumn,
  FieldGroupRes,
  FieldRes,
  FieldTypeEnum,
  MeteDataRes,
  QuestionnaireFieldRes,
  TermReq,
} from 'packages/sdks-next/growp'
import {
  BaseComponentsConfig,
  ButtonConfig,
  DateTypeEnum,
  Option,
  ReqWithMetaData,
  SelectTypeEnum,
} from './types'
import { createEnumHelper, isArray, merge, omit } from '@seiue/util'
import { IconParkName } from 'packages/components/GrowpIcon/types'
import { valueToLabel as userValueToLabel } from 'packages/utils/map'
import humps from 'humps'

export const CommonTopic: BaseComponentsConfig[] = [
  {
    groupName: '基础组件',
    buttonConfigs: [
      {
        id: 1,
        type: FieldTypeEnum.String,
        iconName: 'Single',
        text: '单行文本',
      },
      {
        id: 2,
        type: FieldTypeEnum.Text,
        iconName: 'UnorderedList',
        text: '多行文本',
      },
      { id: 3, type: FieldTypeEnum.Single, iconName: 'Record', text: '单选' },
      {
        id: 4,
        type: FieldTypeEnum.Multi,
        iconName: 'MultipleChoice',
        text: '多选',
      },
      {
        id: 5,
        type: FieldTypeEnum.Attachment,
        iconName: 'Appendix',
        text: '附件',
      },
      { id: 6, type: FieldTypeEnum.Integer, iconName: 'Num', text: '数字' },
      {
        id: 7,
        type: FieldTypeEnum.DateRange,
        iconName: 'Calendar',
        text: '日期区间',
      },
      { id: 8, type: FieldTypeEnum.Time, iconName: 'Time', text: '时间' },
      {
        id: 9,
        type: FieldTypeEnum.Address,
        iconName: 'Address',
        text: '省市县',
      },
      { id: 10, type: FieldTypeEnum.Product, iconName: 'Table', text: '复合' },
    ],
  },
  {
    groupName: '辅助组件',
    buttonConfigs: [
      {
        id: 11,
        type: FieldTypeEnum.Describe,
        iconName: 'Help',
        text: '文本描述',
      },
      {
        id: 12,
        type: FieldTypeEnum.PageBreak,
        iconName: 'Paging',
        text: '分页',
      },
    ],
  },
]

/**
 * 用户字段-自定义字段图标映射
 */
export const typeToIconName = createEnumHelper(() => ({
  [FieldTypeEnum.String]: 'Single',
  [FieldTypeEnum.Text]: 'UnorderedList',
  [FieldTypeEnum.Single]: 'Record',
  [FieldTypeEnum.Multi]: 'MultipleChoice',
  [FieldTypeEnum.Attachment]: 'Appendix',
  [FieldTypeEnum.Integer]: 'Num',
  [FieldTypeEnum.DateRange]: 'Calendar',
  [FieldTypeEnum.Time]: 'Time',
  [FieldTypeEnum.Address]: 'Address',
  [FieldTypeEnum.Product]: 'Table',
  [FieldTypeEnum.Describe]: 'Help',
  [FieldTypeEnum.PageBreak]: 'Paging',
  [FieldTypeEnum.Date]: 'Time',
}))

export const typeToLabel = createEnumHelper(() => ({
  [FieldTypeEnum.String]: '单行文本',
  [FieldTypeEnum.Text]: '多行文本',
  [FieldTypeEnum.Single]: '单选',
  [FieldTypeEnum.Multi]: '多选',
  [FieldTypeEnum.Attachment]: '附件',
  [FieldTypeEnum.Integer]: '数字',
  [FieldTypeEnum.DateRange]: '日期区间',
  [FieldTypeEnum.Time]: '时间',
  [FieldTypeEnum.Address]: '省市县',
  [FieldTypeEnum.Product]: '复合',
  [FieldTypeEnum.Describe]: '',
  [FieldTypeEnum.PageBreak]: '分页',
  [FieldTypeEnum.Date]: '日期',
}))

/**
 * 问卷数据修改
 *
 * @param param.questionnaires - 问卷结构所有数据
 * @param param.currentIndex - 当前选中题目的索引
 * @param param.newQuestionnaire - 当前选中题目的最新相关数据
 * @returns 修改后的数据
 */
export const changeQuestionnaireData = (
  questionnaires: ReqWithMetaData[] | undefined,
  currentIndex: number,
  newQuestionnaire: ReqWithMetaData
) => {
  const preQuestionnaires = questionnaires || []
  preQuestionnaires.splice(currentIndex, 1, newQuestionnaire)
  return preQuestionnaires
}

/**
 * 修改metadata中的数据
 *
 * @param questionnaires - 所有问卷题目数据
 * @param currentIndex - 当前题目索引
 * @param metaDataParams - 要修改的metadata数据
 * @returns 修改后的问卷数据
 */
export const changeMetaData = (
  questionnaires: ReqWithMetaData[] | undefined,
  currentIndex: number,
  metadataParams: MeteDataRes
) => {
  const preQuestionnaires = questionnaires || []
  const currentQuestionnaire = preQuestionnaires[currentIndex]
  preQuestionnaires.splice(currentIndex, 1, {
    ...currentQuestionnaire,
    metadata: {
      ...currentQuestionnaire.metadata,
      ...metadataParams,
    },
  })
  return preQuestionnaires
}

/**
 * 单选多选默认选项
 */
export const BaseOptions: Option[] = [
  {
    name: '选项1',
    label: '选项1',
    value: '选项1',
  },
  {
    name: '选项2',
    label: '选项2',
    value: '选项2',
  },
]

/**
 * 问卷题目项基础配置
 */
export const BaseQuestionnaire: ReqWithMetaData = {
  type: FieldTypeEnum.String,
  require: false,
  editable: true,
  description: '',
  metadata: {},
}

const getOptionValue = (option: TermReq, editable?: boolean) => {
  if (option.name === '其它') {
    return '其它'
  }
  if (!editable) {
    return option.name || option.id || ''
  }
  return option.id || option.name || ''
}

/**
 * 问卷结构详情接口数据格式化
 * @param originData
 */
export const questionnaireDataFormat = (
  originData: QuestionnaireFieldRes[]
) => {
  const finalData = originData.map((item) => {
    let formatChildren
    let formatOptions
    if (item.type === FieldTypeEnum.Product) {
      formatChildren = item.children?.map((child) => {
        const childOptions = child.options || []
        const formatOptions = childOptions
          .filter((i) => !i.deletedAt)
          .map((option) => {
            return {
              ...option,
              label: userValueToLabel[item.name]
                ? userValueToLabel[item.name](option.name)
                : option.name,
              value: getOptionValue(option, child.editable),
            }
          })
        return {
          ...child,
          name: humps.camelize(child.name),
          options: formatOptions,
          isUserField: !!child.groupId,
        }
      })
    } else {
      const options = item.options || []
      formatOptions = options
        .filter((i) => !i.deletedAt)
        .map((option) => {
          return {
            ...option,
            label: userValueToLabel[item.name]
              ? userValueToLabel[item.name](option.name)
              : option.name,
            value: getOptionValue(option, item.editable),
          }
        })
    }
    return {
      ...item,
      name: humps.camelize(item.name),
      options: formatOptions,
      children: formatChildren,
      isUserField: !!item.groupId,
    }
  })
  return finalData
}

/**
 * 根据题目类型生成对应的metadata
 *
 * @param type - 题目类型
 * @returns metadata
 */
export const generatorMetaData = (type: FieldTypeEnum) => {
  let metadata = {}
  if ([FieldTypeEnum.Single, FieldTypeEnum.Multi].includes(type)) {
    metadata = { selectType: SelectTypeEnum.Checked }
  }
  if (
    [FieldTypeEnum.Time, FieldTypeEnum.DateRange, FieldTypeEnum.Date].includes(
      type
    )
  ) {
    metadata = { dateType: DateTypeEnum.Date }
  }
  if ([FieldTypeEnum.Integer].includes(type)) {
    metadata = { precision: 1 }
  }
  if (type === FieldTypeEnum.Attachment) {
    metadata = {
      maxCount: 6,
      minCount: 1,
      fileSizeLimit: 4,
    }
  }
  return metadata
}

/**
 * 生成基础的问卷题目配置
 *
 * @param type - 题目类型
 * @returns 基础配置
 */
export const baseConfigQenerator = (type: FieldTypeEnum) => {
  const metadata = generatorMetaData(type)
  return {
    ...BaseQuestionnaire,
    options: [FieldTypeEnum.Single, FieldTypeEnum.Multi].includes(type)
      ? BaseOptions
      : undefined,
    metadata,
    groupId: 0,
  }
}

/**
 * 生成指定长度的下拉框选项
 *
 * @param maxNum - 选项长度
 * @returns Options
 */
export const createOptions = (maxNum: number) => {
  return Array(maxNum)
    .fill(1)
    .map((item, i) => {
      return {
        label: i + 1,
        value: i + 1,
      }
    })
}

export const createMultiOptions = (len: number) => {
  const options = []
  for (let i = 1; i <= len; i++) {
    options.push({
      label: `${i}`,
      value: `${i}`,
    })
  }
  options.push({
    label: '不限制',
    value: 0,
  })
  return options
}

/**
 * 数字类型精度选项
 */
export const precisionOptions = [
  { label: '十分位（1.0）', value: '1' },
  { label: '百分位（1.00）', value: '2' },
  { label: '千分位（1.000）', value: '3' },
]

/**
 * option根据value匹配label
 */
export const valueToLabel = (
  options: { label: string; value: string }[],
  value: string
) => {
  let label = value
  options.map((option) => {
    if (option.value === value) {
      label = option.label
    }
  })
  return label
}

/**
 * 判断选项中是否含有其它项
 *
 * @param options - 所有选项
 * @returns 判断结果
 */
export const isOptionsIncludeOther = (options: Option[]) => {
  let flag = false
  options.map((option: Option) => {
    if (option.value === '其它') {
      flag = true
    }
  })
  return flag
}

export const nameToIcon: { [key: string]: IconParkName } = {
  name: 'User',
  gender: 'Gender',
  graduate: 'Record',
  avatar: 'Picture',
  idcard: 'Num',
  email: 'Email',
  ['idcard_type']: 'IdCard',
  'guardians.0.pupil_type': 'Branch',
  'guardians.0.name': 'Guardian',
  'guardians.0.phone': 'Phone',
  'guardians.1.pupil_type': 'Branch',
  'guardians.1.name': 'Guardian',
  'guardians.1.phone': 'Phone',
}

/**
 * 分组内字段格式化
 *
 * @param child - 字段对象
 * @returns 格式化的对象
 */
export const fieldFormat = (
  child: FieldRes | ChildFieldResColumn
): ButtonConfig => {
  const metadata = merge(generatorMetaData(child.type), child.metadata)
  const formatChildren =
    child instanceof FieldRes && child.children
      ? child.children.map((cc) => {
          return fieldFormat(cc)
        })
      : []

  return {
    ...BaseQuestionnaire,
    editable: child.editable,
    id: child.id,
    groupId: child.groupId,
    type: child.type,
    iconName: nameToIcon[child.name] || typeToIconName.get(child.type),
    name: child.name,
    text: child.label,
    label: child.label,
    description: child.description,
    metadata: {
      ...metadata,
      showDescription: !!child.description,
    },
    isUserField: true,
    options: child.options
      ?.filter((i) => !i.deletedAt)
      .map((option) => {
        return {
          label: userValueToLabel[child.name]
            ? userValueToLabel[child.name](option.name)
            : option.name,
          value: option.name,
          name: option.name,
        }
      }),
    children: formatChildren,
  }
}

/**
 * 接口返回的用户字段格式化
 */
export const groupsDataFormat = (
  groups: FieldGroupRes[]
): BaseComponentsConfig[] => {
  return groups.map((group) => {
    const groupName = group.name
    const groupChildren = group.questionnaireFields || []
    const buttonConfigs = groupChildren.map((child) => {
      return fieldFormat(child)
    })
    return {
      groupName,
      buttonConfigs,
    }
  })
}

/**
 * API返回的options格式化
 */
export const optionsFormat = (originOptions: TermReq[]): Option[] => {
  return originOptions.map((option) => {
    return {
      label: option.name,
      value: option.id || option.name,
      id: option.id,
      name: option.name,
    }
  })
}

/**
 * 为题目添加序号 排除分页和描述
 *
 * @param originData - 原始数据
 * @returns 添加序号后的数据
 */
export const addSerialNumber = (originData: ReqWithMetaData[]) => {
  let num = 0
  return originData.map((origin) => {
    if (
      ![FieldTypeEnum.Describe, FieldTypeEnum.PageBreak].includes(origin.type)
    ) {
      num++
    }
    return {
      ...origin,
      serialNumber: num,
    }
  })
}

/**
 * 获取到所有用户字段的id
 *
 * @param groups
 */
export const getAllUserFieldId = (groups: FieldGroupRes[]) => {
  const ids: number[] = []
  groups.map((group) => {
    if (group.fields) {
      group.fields.map((field) => {
        ids.push(field.id)
      })
    }
  })
  return ids
}

/**
 * 过滤出问卷中已经使用的用户字段ID
 * @param questionnaires
 * @returns
 */
export const getUsedUserIds = (questionnaires: ReqWithMetaData[]) => {
  const usedUserIds: number[] = []
  questionnaires.map((questionnaire) => {
    if (!!questionnaire.groupId && questionnaire.id) {
      usedUserIds.push(questionnaire.id)
    }
  })
  return usedUserIds
}

const copyOptionFormat = (options?: TermReq[]) => {
  return isArray(options)
    ? options.map((opt) => {
        return {
          ...omit(opt, ['id', 'parentId']),
        }
      })
    : []
}

/**
 * 复制的内容格式化（已保存的题目会生成id,name等内容，复制时需要过滤掉）
 */
export const copyItemFormat = (item: ReqWithMetaData) => {
  const children = isArray(item.children)
    ? item.children.map((child) => {
        return {
          ...omit(child, ['name', 'id', 'parentId']),
          options: copyOptionFormat(child.options),
        }
      })
    : []

  const finalItem = {
    ...omit(item, ['id', 'name', 'children', 'options']),
    children,
    options: copyOptionFormat(item.options),
  }
  return finalItem
}
